import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ColumnDefinition } from '../../../core/model/column-definition';
import { ExpandedListViewItemDefinition } from '../../../core/model/expanded-list-view-item';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { ArticleService } from '../../../rent/service/data/article.service';
import { CompanyService } from '../../../rent/service/data/company.service';
import { PropertyService } from '../../../rent/service/data/property.service';
import { ReportService } from '../../service/data/report.service';
import { canSubmitReportForm } from '../../utility/report-form-validation';

@Component({
    selector: 'app-month-analysis-amount',
    templateUrl: './month-analysis-amount.component.html',
    styleUrls: ['./month-analysis-amount.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MonthAnalysisAmountComponent implements OnInit {
  constructor(
    private _cdf: ColumnDefinitionFactory,
    private _companyService: CompanyService,
    private _propertyService: PropertyService,
    private _articleService: ArticleService,
    private _nativeWindow: NativeWindowService,
    private _dataService: ReportService,
    fb: FormBuilder,
    fcf: FormControlExFactory
  ) {
    const todayDate = new Date();

    this.form = fb.group({
      company: fcf.create('COMMON.COMPANY', ['All']),
      property: fcf.create('COMMON.PROPERTY', 'All'),
      article: fcf.create('ARTICLE.EDIT_HEADER', 'All'),
      year: fcf.create('REPORT.DISPLAY_YEAR', todayDate.getFullYear()),
      groupLevel: fcf.create('REPORT.GROUP_LEVEL', 'rentalobject'),
      displaySource: fcf.create('COMMON.DISPLAY', 'rentperiod'),
    });

    this.columnDefinitions = [
      this._cdf.createMoney('MONTH.JANUARY', 'januaryAmount'),
      this._cdf.createMoney('MONTH.FEBRUARY', 'februaryAmount'),
      this._cdf.createMoney('MONTH.MARCH', 'marchAmount'),
      this._cdf.createMoney('MONTH.APRIL', 'aprilAmount'),
      this._cdf.createMoney('MONTH.MAY', 'mayAmount'),
      this._cdf.createMoney('MONTH.JUNE', 'juneAmount'),
      this._cdf.createMoney('MONTH.JULY', 'julyAmount'),
      this._cdf.createMoney('MONTH.AUGUST', 'augustAmount'),
      this._cdf.createMoney('MONTH.SEPTEMBER', 'septemberAmount'),
      this._cdf.createMoney('MONTH.OCTOBER', 'octoberAmount'),
      this._cdf.createMoney('MONTH.NOVEMBER', 'novemberAmount'),
      this._cdf.createMoney('MONTH.DECEMBER', 'decemberAmount'),
    ];
  }

  columnDefinitions: ColumnDefinition[] = [];
  companies: { name: string; value: string }[];
  realEstateProperties: { name: string; value: string }[];
  articles: { name: string; value: string }[];
  yearSelection: { name: string; value: number }[] = [];
  groupSelection: { name: string; value: string }[];
  displaySelection: { name: string; value: string }[];
  dataContext: ExpandedListViewItemDefinition[] = [];
  form: FormGroup;
  today = new Date();
  private _subscription: Subscription;
  protected expandAllRows: boolean = false;

  ngOnInit() {
    this._companyService.getList().subscribe(data => {
      this.companies = [{ name: 'ALL_TEXT', value: 'All' }];
      data.forEach(item => {
        this.companies.push({ name: item.toString(), value: item.id });
      });

      if (this.companies.length >= 2) {
        this.form.controls['company'].setValue([this.companies[1].value]);
      }
    });

    this._propertyService.getList().subscribe(data => {
      this.realEstateProperties = [{ name: 'ALL_TEXT', value: 'All' }];
      data.forEach(item => {
        this.realEstateProperties.push({ name: item.toString(), value: item.id });
      });
    });

    this._articleService.getList().subscribe(data => {
      this.articles = [{ name: 'ALL_TEXT', value: 'All' }];
      data.forEach(item => {
        this.articles.push({ name: item.toString(), value: item.id });
      });
    });

    const currentYear = new Date().getFullYear();
    for (let offset = -3; offset <= 10; offset++)
      this.yearSelection.push({ name: (currentYear + offset).toString(), value: currentYear + offset });

    this.groupSelection = [{ name: 'COMMON.COMPANY', value: 'company' }];
    this.groupSelection.push({ name: 'COMMON.PROPERTY', value: 'realestateproperty' });
    this.groupSelection.push({ name: 'COMMON.RENTAL_OBJECT', value: 'rentalobject' });

    this.displaySelection = [{ name: 'REPORT.DISPLAY_OPTION_RENT_PERIOD_MONTH_ANALYSIS', value: 'rentperiod' }];
    this.displaySelection.push({ name: 'REPORT.DISPLAY_OPTION_RENT_DUEDATE_MONTH_ANALYSIS', value: 'rentduedate' });
    this.displaySelection.push({ name: 'RENTAL_OBJECT_FORM.OBJECT_RENT', value: 'rentalobjectrent' });
    this.displaySelection.push({ name: 'REPORT.DISPLAY_OPTION_INVOICED_MONTH_ANALYSIS', value: 'invoicedperiod' });
  }

  onPrintPages() {
    if (!canSubmitReportForm(this.form)) return;
    window.print();
  }

  exportExcel(): void {
    if (!canSubmitReportForm(this.form)) return;
    if (this._subscription) this._subscription.unsubscribe();

    this._subscription = this._dataService.getMonthAnalysisExcel(this.form.value).subscribe(file => {
      this._nativeWindow.save(file);
    });
  }

  ngOnDestroy(): void {
    if (this._subscription) this._subscription.unsubscribe();
  }

  onSubmit() {
    if (!canSubmitReportForm(this.form)) return;
    this.expandAllRows = false;

    this._dataService.getMonthAnalysisAmountReport(this.form.value).subscribe(data => {
      this.dataContext = data
        ? data.map(overview => {
            const model = new ExpandedListViewItemDefinition();
            model.caption = overview.level1Name;
            model.columnDefinitions = [
              this._cdf.create('ARTICLE.EDIT_HEADER', 'level2Name'),
              this._cdf.createMoney('MONTH.JANUARY', 'januaryAmount'),
              this._cdf.createMoney('MONTH.FEBRUARY', 'februaryAmount'),
              this._cdf.createMoney('MONTH.MARCH', 'marchAmount'),
              this._cdf.createMoney('MONTH.APRIL', 'aprilAmount'),
              this._cdf.createMoney('MONTH.MAY', 'mayAmount'),
              this._cdf.createMoney('MONTH.JUNE', 'juneAmount'),
              this._cdf.createMoney('MONTH.JULY', 'julyAmount'),
              this._cdf.createMoney('MONTH.AUGUST', 'augustAmount'),
              this._cdf.createMoney('MONTH.SEPTEMBER', 'septemberAmount'),
              this._cdf.createMoney('MONTH.OCTOBER', 'octoberAmount'),
              this._cdf.createMoney('MONTH.NOVEMBER', 'novemberAmount'),
              this._cdf.createMoney('MONTH.DECEMBER', 'decemberAmount'),
              this._cdf.createMoney('COMMON.SUM', 'totalAmount'),
            ];
            model.dataContext = overview;
            model.itemsSource = overview.monthAnalysisLevel2Models.map(x => {
              return {
                level2Name: x.level2Name,
                januaryAmount: x.januaryAmount,
                februaryAmount: x.februaryAmount,
                marchAmount: x.marchAmount,
                aprilAmount: x.aprilAmount,
                mayAmount: x.mayAmount,
                juneAmount: x.juneAmount,
                julyAmount: x.julyAmount,
                augustAmount: x.augustAmount,
                septemberAmount: x.septemberAmount,
                octoberAmount: x.octoberAmount,
                novemberAmount: x.novemberAmount,
                decemberAmount: x.decemberAmount,
                totalAmount: x.totalAmount,
              };
            });

            return model;
          })
        : [];
    });
  }

  toggleExpandAllRows() {
    this.expandAllRows = !this.expandAllRows;
  }
}
